import React, { useState, useEffect, useRef, useMemo } from "react";
import NavbarFacach from "../components/NavbarFacach";
import FooterFacach from "../components/FooterFacach";
import { Container, Row, Col, Image, Spinner, Modal, Badge, Form } from "react-bootstrap";
import "../components/financieros.css";
import SocialBar from "../components/SocialBar";
import logoFacach from "../assets/fotos/ISOLOO.png";
import { useSiteSettings } from '../hooks/useSiteSettings';
import fallbackBanner from '../assets/imagen.jpg';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const API_URL = "https://facach.coop.hn/api";

const FALLBACK_DOCS = [
  { id: 1, title: "Estados Financieros 2023", year: 2023, file_url: "https://facach.coop.hn/_files/ugd/3a8449_5e1f5ddc28f84729b2a16a9ab298dfc5.pdf" },
  { id: 2, title: "Informe de Auditoría",     year: 2023, file_url: "https://facach.coop.hn/_files/ugd/3a8449_5e1f5ddc28f84729b2a16a9ab298dfc5.pdf" },
  { id: 3, title: "Balance General",          year: 2023, file_url: "https://facach.coop.hn/_files/ugd/3a8449_5e1f5ddc28f84729b2a16a9ab298dfc5.pdf" },
  { id: 4, title: "Estados de Resultados",    year: 2023, file_url: "https://facach.coop.hn/_files/ugd/3a8449_5e1f5ddc28f84729b2a16a9ab298dfc5.pdf" },
];

function formatSize(bytes) {
  if (!bytes) return null;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function PdfCover({ url, title }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!url) { setError(true); setLoading(false); return; }
    let cancelled = false;
    const render = async () => {
      try {
        const pdf    = await pdfjsLib.getDocument({ url, withCredentials: false }).promise;
        const page   = await pdf.getPage(1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx    = canvas.getContext('2d');
        const vp     = page.getViewport({ scale: 1.2 });
        canvas.width  = vp.width;
        canvas.height = vp.height;
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) { setError(true); setLoading(false); }
      }
    };
    render();
    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <div className="doc-cover-placeholder">
        <img src={logoFacach} alt={title} className="doc-placeholder-logo" />
        <div className="doc-placeholder-title">{title}</div>
      </div>
    );
  }

  return (
    <div className="doc-canvas-wrap">
      {loading && (
        <div className="doc-cover-loading">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      )}
      <canvas ref={canvasRef} className="doc-canvas" style={{ opacity: loading ? 0 : 1 }} />
    </div>
  );
}

function DocCard({ doc, onVerMas }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`doc-card ${hovered ? 'doc-card--hovered' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onVerMas(doc)}
      role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onVerMas(doc)}
    >
      <div className="doc-folder-tab" />
      <div className="doc-folder-body">
        <div className="doc-preview">
          <PdfCover url={doc.file_url} title={doc.title} />
        </div>
        <div className="doc-overlay">
          <div className="doc-overlay-icon">📄</div>
          <div className="doc-overlay-text">Ver documento</div>
        </div>
      </div>
      <div className="doc-sombra" />
    </div>
  );
}

function DocModal({ doc, onHide }) {
  if (!doc) return null;
  return (
    <Modal show={!!doc} onHide={onHide} centered size="md">
      <Modal.Header closeButton style={{ borderBottom: "3px solid #1a3a6b" }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 800, color: "#1a3a6b" }}>
          {doc.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          <div style={{ width: 120, flexShrink: 0, borderRadius: 8, overflow: "hidden", border: "1px solid #dde3ef" }}>
            <PdfCover url={doc.file_url} title={doc.title} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 12 }}>
              <Badge bg="primary" style={{ marginRight: 8 }}>PDF</Badge>
              {doc.year && <Badge bg="secondary">{doc.year}</Badge>}
            </div>
            {formatSize(doc.file_size_bytes) && (
              <div style={{ fontSize: 13, color: "#7a8aaa", marginBottom: 16 }}>
                <strong>Tamaño:</strong> {formatSize(doc.file_size_bytes)}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                className="btn btn-primary btn-sm" style={{ textAlign: "center" }}>
                 Ver documento
              </a>
              <a href={doc.file_url} download
                className="btn btn-outline-primary btn-sm" style={{ textAlign: "center" }}>
                Descargar PDF
              </a>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

const Financieros = () => {
  const { settings } = useSiteSettings();
  const bannerSrc = settings.banner_image
    ? `https://facach.coop.hn${settings.banner_image}`
    : fallbackBanner;

  const [docs, setDocs]         = useState(FALLBACK_DOCS);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [query, setQuery]       = useState("");
  const [anioFiltro, setAnioFiltro] = useState("todos");

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const res  = await fetch(`${API_URL}/documents/financial_statements`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.ok && json.data?.length > 0) setDocs(json.data);
      } catch (err) {
        console.error("Error cargando estados financieros:", err.message);
      } finally { setLoading(false); }
    };
    fetchDocs();
  }, []);

  const aniosDisponibles = useMemo(() => {
    const set = new Set(docs.map(d => d.year).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [docs]);

  const docsFiltrados = useMemo(() => {
    return docs.filter(d => {
      const coincideTexto = d.title.toLowerCase().includes(query.trim().toLowerCase());
      const coincideAnio  = anioFiltro === "todos" ? true : String(d.year) === String(anioFiltro);
      return coincideTexto && coincideAnio;
    });
  }, [docs, query, anioFiltro]);

  return (
    <Container fluid className="p-0 theme-dark financieros-page">
      <NavbarFacach />

      {/* Banner */}
      <div className="banner-wrapper">
        <Image src={bannerSrc} alt="Estados Financieros - FACACH" fluid className="banner-image" />
        <div className="banner-overlay">
          <h1 className="banner-title">ESTADOS FINANCIEROS</h1>
          <p className="banner-description">
            Consulta los estados financieros y documentos relacionados, disponibles para su descarga.
          </p>
        </div>
      </div>

      <Container className="mt-4">

        {/* Filtros — igual que Memorias */}
        <section className="filtros-section" aria-label="Filtros de búsqueda">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={8}>
              <Form.Group controlId="buscarFinanciero">
                <Form.Label className="filtro-label">Buscar por título</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej.: Estados Financieros 2023"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group controlId="filtrarAnio">
                <Form.Label className="filtro-label">Año</Form.Label>
                <Form.Select value={anioFiltro} onChange={e => setAnioFiltro(e.target.value)}>
                  <option value="todos">Todos</option>
                  {aniosDisponibles.map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </section>

        {/* Documentos */}
        <section className="documentos-section">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : docsFiltrados.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1a3a6b" }}>Sin resultados</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                No encontramos documentos que coincidan con tus filtros.
              </div>
            </div>
          ) : (
            <div className="docs-grid">
              {docsFiltrados.map(doc => (
                <div key={doc.id} className="docs-grid-item">
                  <DocCard doc={doc} onVerMas={setSelected} />
                  <div className="doc-info">
                    <div className="doc-titulo">{doc.title}</div>
                    {doc.year && <div className="doc-anio">{doc.year}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </Container>

      <SocialBar position="right" top="35%" size={42} rounded={true} />
      <FooterFacach />

      <DocModal doc={selected} onHide={() => setSelected(null)} />
    </Container>
  );
};

export default Financieros;